import React, {CSSProperties, useEffect, useRef, useState} from 'react'
import ReactDOMServer from 'react-dom/server.browser.js'
import type {GeoJsonObject} from 'geojson'

import geolocation from '../../utils/geolocation.js'
import L from '../../../src/components/map/leaflet.js'
import {baseMapOptions, createTileLayer, defaultCenter, defaultZoom, fitBoundsSafe} from '../../../src/components/map/setup.js'
import {validPosition} from '../../../src/components/map/geometry.js'
import useLeafletMap from '../../../src/components/map/hooks/useLeafletMap.js'
import useLayerGroup from '../../../src/components/map/hooks/useLayerGroup.js'
import useLatest from '../../../src/components/map/hooks/useLatest.js'
import MapAttribution from '../../../src/components/map/controls/MapAttribution.js'
import MapLoadingOverlay from '../../../src/components/map/controls/MapLoadingOverlay.js'
import MapControlCard from '../../../src/components/map/controls/MapControlCard.js'
import MapControlButton from '../../../src/components/map/controls/MapControlButton.js'
import {mapContainerStyle, mapElementStyle, mapSpacing, mapZIndex} from '../../../src/components/map/styles.js'
import {Address, AddressBundle} from '../index.js'
import {Colors} from '../../../src/components/UI/index.js'
import {buildRouteMarkers, getNextStopPosition, getStopInfo, isSameStop, LatLng, RouteMapRoute, RouteMarker, StopKey} from './routeMapModel.js'

interface CourierLocation extends LatLng {
    accuracy: number
    heading?: number | null
}

type FollowMode = 'messenger' | 'nextStop' | null

interface RouteMapProps {
    reseller: {settings?: {companyAddress?: {position?: {lat?: number, lng?: number}}}}
    user: unknown
    route: RouteMapRoute
    loading?: boolean
    /** Open the info of this stop when the map opens */
    orderId?: string
    addressIndex?: number
    /** Show the finish when the map opens */
    focusEndPoint?: boolean
    style?: CSSProperties
    disabled?: boolean
    onClickPickupDeliver: (stop: unknown, value: string) => void
    onClickNavigation: (user: unknown, route: RouteMapRoute, stop: unknown) => void
}

const fitOptions = {padding: [50, 50] as L.PointTuple, animate: true}

// Zoom on a single point (the courier while following, a stop without a known location) and the most a fit zooms
// in (the courier next to the next stop); fitBounds would zoom in all the way, to a few metres
const pointZoom = 17

const headingIcon = L.divIcon({
    html: ReactDOMServer.renderToString(
        <div
            data-heading
            style={{
                display: 'none',
                position: 'absolute',
                top: 5,
                left: -11,
                borderBottom: '30px solid #53b4fe',
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                height: 0,
                width: 35,
                opacity: 0.5,
                transform: 'rotate(0deg)',
                transformOrigin: '50% 0%',
                transition: 'all .5s ease',
                pointerEvents: 'none'
            }}
        >
        </div>
    ),
    className: 'dummy' // necessary to remove default style
})

const locationIcon = L.divIcon({
    html: ReactDOMServer.renderToString(
        <div
            style={{
                position: 'absolute',
                top: -5,
                left: -5,
                height: 20,
                width: 20,
                background: Colors.infoBright,
                borderRadius: '50%',
                borderWidth: 3,
                borderColor: 'white',
                borderStyle: 'solid',
                pointerEvents: 'none'
            }}
        />
    ),
    className: 'dummy' // necessary to remove default style
})

const markerStyle = (marker: RouteMarker, selected: boolean): L.CircleMarkerOptions => ({
    color: selected ? Colors.backgroundDark : Colors.white,
    weight: marker.checkered || selected ? 2 : 1
})

const readFollowMode = (): FollowMode => localStorage.followNextStop === 'true' ? 'nextStop' : localStorage.followMessenger === 'true' ? 'messenger' : null

const storeFollowMode = (mode: FollowMode) => {
    localStorage.followNextStop = mode === 'nextStop' ? 'true' : ''
    localStorage.followMessenger = mode === 'messenger' ? 'true' : ''
}

/**
 * The courier's map of the route: the open stops, start and finish, the route's line and the courier's own
 * location (with heading and accuracy) once the route is started. Tapping a stop opens its info below the map. The
 * map can follow the courier, or the courier and the next stop; moving the map stops following.
 */
export default function RouteMap(props: RouteMapProps) {
    const {reseller, user, route, loading, style = {}, onClickPickupDeliver, onClickNavigation, disabled} = props
    const containerRef = useRef<HTMLDivElement>(null)
    const [follow, setFollow] = useState<FollowMode>(readFollowMode)
    const [selected, setSelected] = useState<StopKey | null>(null)
    const location = useRef<CourierLocation | null>(null)
    // What the first location is fitted with: the stops, or the finish or stop the map opened on
    const fitPoints = useRef<LatLng[]>([])
    const programmaticMove = useRef(false)
    const programmaticMoveTimeout = useRef(0)

    const clearProgrammaticMove = () => {
        clearTimeout(programmaticMoveTimeout.current)
        programmaticMove.current = false
    }

    const map = useLeafletMap(containerRef, {...baseMapOptions, zoomSnap: 0}, (map) => {
        map.setView(validPosition(reseller?.settings?.companyAddress?.position) || defaultCenter, defaultZoom)

        // Leaflet's default tile loading, as the courier app always had
        createTileLayer({updateWhenZooming: true, updateWhenIdle: L.Browser.mobile}).addTo(map)

        map.on('click', () => {
            setSelected(null)
            latest.current.stopFollowing()
        })

        // Dragging only comes from the courier, so it never needs the programmatic guard
        map.on('dragstart', () => latest.current.stopFollowing())

        // Zooming also fires for our own fits, so ignore those
        map.on('zoomstart', () => {
            if (!programmaticMove.current) {
                latest.current.stopFollowing()
            }
        })

        map.on('moveend zoomend', clearProgrammaticMove)

        return () => clearTimeout(programmaticMoveTimeout.current)
    })

    // Marks the move as ours, so the zoomstart handler doesn't take it for the courier zooming
    const fitMap = (points: Array<LatLng | null>, options: L.FitBoundsOptions = {}) => {
        if (!map) {
            return
        }

        programmaticMove.current = true

        // In case the move is a no-op and moveend/zoomend never fires
        clearTimeout(programmaticMoveTimeout.current)
        programmaticMoveTimeout.current = window.setTimeout(clearProgrammaticMove, 1000)

        if (!fitBoundsSafe(map, points, {...options, padding: [50, 50], maxZoom: pointZoom, pointZoom})) {
            clearProgrammaticMove()
        }
    }

    const stopFollowing = () => {
        if (latest.current.follow) {
            setFollow(null)
            storeFollowMode(null)
        }
    }

    const openStop = (marker: RouteMarker) => {
        setSelected(marker.stop)
        fitPoints.current = [marker.position]
        fitMap([marker.position, location.current], fitOptions)
    }

    const latest = useLatest({props, follow, selected, stopFollowing, openStop, fitMap})

    const markerGroup = useLayerGroup(map)
    const lineGroup = useLayerGroup(map)
    const markerLayers = useRef<Array<{marker: RouteMarker, layer: L.CircleMarker}>>([])
    const lineGeometry = useRef<object | null>(null)
    const builds = useRef(0)

    // The markers are rebuilt when the route changes (a few dozen, cheap), the line only when its geometry does
    useEffect(() => {
        if (!map || !markerGroup || !lineGroup) {
            return
        }

        const {props, selected} = latest.current
        const {route, orderId, addressIndex, focusEndPoint} = props
        const hadMarkers = markerLayers.current.length > 0
        const markers = buildRouteMarkers(route)

        // Before the markers, which are drawn on the same canvas in the order they are added
        if (route?.geometry !== lineGeometry.current) {
            lineGeometry.current = route?.geometry || null
            lineGroup.clearLayers()

            try {
                if (route?.geometry) {
                    lineGroup.addLayer(L.geoJSON(route.geometry as GeoJsonObject, {style: {color: Colors.grey60, weight: 2, opacity: 0.8}}))
                }
            } catch (error) {
                // A route without a valid line still shows its stops
                console.error(error)
            }
        }

        markerGroup.clearLayers()

        markerLayers.current = markers.map((marker) => {
            const layer = L.circleMarker(marker.position, {
                text: marker.label,
                fontColor: Colors.white,
                radius: 10,
                fillColor: marker.color,
                checkered: marker.checkered,
                fillOpacity: marker.opacity,
                ...markerStyle(marker, isSameStop(marker.stop, selected))
            })

            layer.on('click', (event) => {
                L.DomEvent.stopPropagation(event)

                if (marker.stop?.orderId) {
                    latest.current.openStop(marker)
                }
            })

            markerGroup.addLayer(layer)
            return {marker, layer}
        })

        if (!markers.length) {
            return
        }

        const firstBuild = builds.current++ === 0
        const endPosition = validPosition(route?.endPoint?.position)
        const openedStop = firstBuild && orderId ? markers.find((marker) => isSameStop(marker.stop, {orderId, addressIndex})) : null

        if (openedStop) {
            latest.current.openStop(openedStop)
        } else if (firstBuild && focusEndPoint && endPosition) {
            // The courier is added when the first location comes in
            fitPoints.current = [endPosition]
            latest.current.fitMap([endPosition, location.current], {padding: [50, 50]})
        } else if (!hadMarkers) {
            fitPoints.current = markers.map((marker) => marker.position)
            latest.current.fitMap([...fitPoints.current, location.current], {padding: [50, 50]})
        }
    }, [map, markerGroup, lineGroup, route])

    useEffect(() => {
        for (const {marker, layer} of markerLayers.current) {
            layer.setStyle(markerStyle(marker, isSameStop(marker.stop, selected)))
        }
    }, [selected])

    // The courier's location, as long as the map is open
    useEffect(() => {
        if (!map) {
            return
        }

        let heading: L.Marker | null = null
        let position: L.Marker | null = null
        let accuracy: L.Circle | null = null

        const watchId = geolocation.watchPosition((err, newLocation) => {
            if (err || !validPosition(newLocation)) {
                return
            }

            const {props, follow, fitMap} = latest.current
            const {route} = props
            const firstLocation = !location.current
            location.current = newLocation

            if (!position) {
                heading = L.marker(newLocation, {icon: headingIcon, interactive: false}).addTo(map)
                position = L.marker(newLocation, {icon: locationIcon, interactive: false}).addTo(map)
                accuracy = L.circle(newLocation, {radius: newLocation.accuracy, weight: 1, interactive: false}).addTo(map)
            }

            heading.setLatLng(newLocation)
            position.setLatLng(newLocation)
            accuracy.setLatLng(newLocation)
            accuracy.setRadius(newLocation.accuracy || 0)

            if (!route?.started) {
                heading.setOpacity(0)
                position.setOpacity(0)
                accuracy.setStyle({opacity: 0})
                return
            }

            heading.setOpacity(1)
            position.setOpacity(1)
            accuracy.setStyle({opacity: 0.6})

            const headingElement = heading.getElement()?.querySelector<HTMLElement>('[data-heading]')

            if (headingElement) {
                if (typeof newLocation.heading === 'number') {
                    headingElement.style.display = 'block'
                    headingElement.style.transform = `rotate(${newLocation.heading}deg)`
                } else {
                    headingElement.style.display = 'none'
                }
            }

            if (follow === 'nextStop') {
                const nextStopPosition = validPosition(getNextStopPosition(route))

                if (nextStopPosition) {
                    fitMap([nextStopPosition, newLocation], fitOptions)
                }
            } else if (follow === 'messenger') {
                fitMap([newLocation], fitOptions)
            } else if (firstLocation) {
                fitMap([...fitPoints.current, newLocation], fitOptions)
            }
        })

        return () => {
            if (watchId !== undefined) {
                geolocation.clearWatch(watchId)
            }

            heading?.remove()
            position?.remove()
            accuracy?.remove()
        }
    }, [map])

    const startFollowing = (mode: FollowMode) => {
        setFollow(mode)
        storeFollowMode(mode)

        if (mode === 'nextStop') {
            const nextStopPosition = validPosition(getNextStopPosition(route))

            if (nextStopPosition) {
                fitMap([nextStopPosition, location.current], fitOptions)
            }
        } else if (location.current) {
            fitMap([location.current], fitOptions)
        }
    }

    const info = getStopInfo(route, selected)

    const orderInfoStyle: CSSProperties = {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        bottom: 80,
        width: '100%',
        zIndex: 8
    }

    return (
        <>
            <div style={{...mapContainerStyle, width: '100vw', flex: 1, ...style}}>
                <div ref={containerRef} style={mapElementStyle} />

                <MapLoadingOverlay loading={loading} />

                <div style={{position: 'absolute', bottom: 45, right: mapSpacing.inset, zIndex: mapZIndex.controls}}>
                    {/* The planning map's buttons, without tooltips on a touch screen; at the bottom right, in thumb reach */}
                    <MapControlCard>
                        <MapControlButton
                            icon='mdi mdi-crosshairs-gps'
                            label='Volg mijn locatie'
                            hideTooltip
                            active={follow === 'messenger'}
                            onClick={() => startFollowing('messenger')}
                        />
                        <MapControlButton
                            icon='mdi mdi-map-marker-distance'
                            label='Volg mijn locatie en de volgende stop'
                            hideTooltip
                            active={follow === 'nextStop'}
                            onClick={() => startFollowing('nextStop')}
                        />
                    </MapControlCard>
                </div>

                <MapAttribution />
            </div>

            <div style={orderInfoStyle}>
                {info?.bundle &&
                    <AddressBundle
                        reseller={reseller}
                        route={route}
                        bundle={info.bundle}
                        onClick={() => onClickPickupDeliver(info.bundle, '')}
                        onClickAddress={(stop) => onClickPickupDeliver(stop, '')}
                        onClickNavigation={(stop) => onClickNavigation(user, route, stop)}
                        disabled={disabled}
                    />
                }

                {info && !info.bundle &&
                    <Address
                        reseller={reseller}
                        address={info.address}
                        onClick={() => onClickPickupDeliver(info.address, '')}
                        onClickNavigation={(address) => onClickNavigation(user, route, address)}
                        disabled={disabled}
                    />
                }
            </div>
        </>
    )
}
